/* Furniture refit: progressive enhancement only. Shopify's native product form,
   variant selection, price updates, media gallery and cart are not intercepted. */
(() => {
  'use strict';
  document.documentElement.classList.add('rf-js');
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollBehavior = () => reducedMotion() ? 'auto' : 'smooth';

  class RFSlider extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.track = this.querySelector('[data-rf-track]');
      this.slides = [...this.querySelectorAll('[data-rf-slide]')];
      if (!this.track || !this.slides.length) return;
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.prev = this.querySelector('[data-rf-prev]');
      this.next = this.querySelector('[data-rf-next]');
      this.dots = [...this.querySelectorAll('[data-rf-dot]')];
      this.prev?.addEventListener('click', () => this.move(-1), { signal });
      this.next?.addEventListener('click', () => this.move(1), { signal });
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index), { signal }));
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      }, { passive: true, signal });
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.findIndex(slide => slide === event.target || slide.contains(event.target));
        if (index >= 0) this.goTo(index, 'auto');
      }, { signal });
      this.observer = new ResizeObserver(() => this.update());
      this.observer.observe(this.track);
      this.setAttribute('data-enhanced', '');
      this.update();
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      this.observer?.disconnect(); cancelAnimationFrame(this.frame);
    }
    get rtl() { return getComputedStyle(this.track).direction === 'rtl'; }
    distance(slide) {
      const track = this.track.getBoundingClientRect();
      const target = slide.getBoundingClientRect();
      return this.rtl ? target.right - track.right : target.left - track.left;
    }
    goTo(index, behavior = scrollBehavior()) {
      const target = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
      if (target) this.track.scrollBy({ left: this.distance(target), behavior });
    }
    move(direction) {
      const step = this.dataset.sliderKind === 'hero' ? 1 : Math.max(1, Math.floor((this.track.clientWidth + 1) / this.slides[0].getBoundingClientRect().width));
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (this.dataset.sliderKind === 'hero') {
        const atEnd = Math.abs(this.track.scrollLeft) >= max - 3;
        const atStart = Math.abs(this.track.scrollLeft) <= 3;
        if (direction > 0 && atEnd) return this.goTo(0);
        if (direction < 0 && atStart) return this.goTo(this.slides.length - 1);
      }
      this.goTo((this.currentIndex || 0) + direction * step);
    }
    update() {
      let index = 0, nearest = Infinity;
      this.slides.forEach((slide, i) => {
        const distance = Math.abs(this.distance(slide));
        if (distance < nearest) { nearest = distance; index = i; }
      });
      this.currentIndex = index;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const position = Math.abs(this.track.scrollLeft);
      const overflow = max > 3;
      if (this.prev) {
        this.prev.hidden = !overflow;
        this.prev.disabled = this.dataset.sliderKind !== 'hero' && position < 3;
      }
      if (this.next) {
        this.next.hidden = !overflow;
        this.next.disabled = this.dataset.sliderKind !== 'hero' && position >= max - 3;
      }
      this.dots.forEach((dot, i) => {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      // Off-screen hero links must not be reachable from the keyboard.
      if (this.dataset.sliderKind === 'hero') {
        this.slides.forEach((slide, i) => { slide.inert = i !== index; });
      }
    }
  }

  class RFTabs extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.tabs = [...this.querySelectorAll('[data-rf-tab]')];
      this.panels = [...this.querySelectorAll('[data-rf-panel]')];
      if (!this.tabs.length) return;
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.querySelector('[data-rf-tablist]')?.setAttribute('role', 'tablist');
      this.tabs.forEach((tab, index) => {
        tab.setAttribute('role', 'tab');
        tab.addEventListener('click', () => this.select(index), { signal });
        tab.addEventListener('keydown', event => {
          const rtl = getComputedStyle(this).direction === 'rtl';
          let next;
          if (event.key === 'ArrowRight') next = index + (rtl ? -1 : 1);
          if (event.key === 'ArrowLeft') next = index + (rtl ? 1 : -1);
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          next = (next + this.tabs.length) % this.tabs.length;
          this.select(next); this.tabs[next].focus();
        }, { signal });
      });
      this.panels.forEach(panel => { panel.setAttribute('role', 'tabpanel'); panel.tabIndex = 0; });
      this.addEventListener('shopify:block:select', event => {
        const index = this.panels.findIndex(panel => panel === event.target || panel.contains(event.target));
        if (index >= 0) this.select(index);
      }, { signal });
      this.setAttribute('data-enhanced', '');
      this.select(0);
    }
    select(index) {
      this.tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === index)); tab.tabIndex = i === index ? 0 : -1; });
      this.panels.forEach((panel, i) => { panel.hidden = i !== index; });
    }
    disconnectedCallback() { this.abort?.abort(); this.abort = null; }
  }

  class RFRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.performed === 'true' || !this.dataset.url || this.loading) return;
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { this.observer.disconnect(); this.load(); }
      }, { rootMargin: '300px' });
      this.observer.observe(this);
    }
    async load() {
      this.loading = true;
      this.abort = new AbortController();
      try {
        const url = new URL(this.dataset.url, window.location.origin);
        if (url.origin !== window.location.origin) return;
        const response = await fetch(url, { signal: this.abort.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Recommendations: HTTP ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const replacement = html.querySelector('rf-recommendations');
        if (replacement?.querySelector('.rf-card')) {
          this.innerHTML = replacement.innerHTML;
          this.dataset.performed = 'true';
          document.dispatchEvent(new CustomEvent('rf:wishlist-sync'));
        }
      } catch (error) {
        // Keep the genuine server-rendered collection fallback if the request fails.
        if (error.name !== 'AbortError' && window.Shopify?.designMode) console.warn('[Furniture refit]', error);
      } finally { this.loading = false; }
    }
    disconnectedCallback() { this.observer?.disconnect(); this.abort?.abort(); }
  }

  for (const [name, constructor] of [['rf-slider', RFSlider], ['rf-tabs', RFTabs], ['rf-recommendations', RFRecommendations]]) {
    if (!customElements.get(name)) customElements.define(name, constructor);
  }

  // Device-local wishlist. Store product handles/paths, never customer information.
  // No innerHTML is built from localStorage; entries are validated and textContent is used.
  const storageKey = 'furniture-refit:wishlist:v1';
  let memory = [];
  const safePath = value => {
    try {
      const url = new URL(value, location.origin);
      return url.origin === location.origin && url.pathname.includes('/products/') ? url.pathname + url.search : null;
    } catch { return null; }
  };
  const safeImage = value => {
    if (!value) return '';
    try {
      const url = new URL(value, location.origin);
      return url.protocol === 'https:' || url.origin === location.origin ? url.href : '';
    } catch { return ''; }
  };
  const validItem = item => item && /^\d+$/.test(String(item.id)) && typeof item.title === 'string' && typeof item.url === 'string' && safePath(item.url);
  function readItems() {
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (Array.isArray(stored)) memory = stored.filter(validItem).slice(0, 100);
    } catch { /* Private browsing or unavailable storage: use session memory. */ }
    return memory;
  }
  function writeItems(items) {
    memory = items.slice(0, 100);
    try { localStorage.setItem(storageKey, JSON.stringify(memory)); } catch { /* Non-persistent memory fallback. */ }
  }
  function root() { return document.getElementById('RFWishlistRoot'); }
  function sync() {
    const holder = root();
    if (!holder) return;
    const ids = new Set(readItems().map(item => String(item.id)));
    document.querySelectorAll('[data-rf-wishlist-add]').forEach(button => {
      const active = ids.has(button.dataset.id);
      const label = active ? holder.dataset.removeLabel : holder.dataset.addLabel;
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', `${label}: ${button.dataset.title || ''}`);
      const text = button.querySelector('[data-rf-wishlist-label]');
      if (text) text.textContent = label;
    });
    document.querySelectorAll('[data-rf-wishlist-count]').forEach(node => { node.textContent = String(ids.size); node.hidden = ids.size === 0; });
  }
  function renderList() {
    const holder = root();
    const list = holder?.querySelector('[data-rf-wishlist-list]');
    if (!list) return;
    const items = readItems();
    list.replaceChildren();
    holder.querySelector('[data-rf-wishlist-empty]').hidden = items.length > 0;
    items.forEach(item => {
      const li = document.createElement('li'); li.className = 'rf-wishlist-item';
      const anchor = document.createElement('a'); anchor.className = 'rf-wishlist-item__link'; anchor.href = safePath(item.url);
      const imageUrl = safeImage(item.image);
      if (imageUrl) {
        const image = document.createElement('img'); image.src = imageUrl; image.alt = ''; image.width = 76; image.height = 76; image.loading = 'lazy'; anchor.append(image);
      }
      const title = document.createElement('span'); title.textContent = item.title; anchor.append(title);
      const button = document.createElement('button'); button.type = 'button'; button.className = 'rf-wishlist-item__remove'; button.dataset.rfWishlistRemove = String(item.id); button.textContent = holder.dataset.removeLabel; button.setAttribute('aria-label', `${holder.dataset.removeLabel}: ${item.title}`);
      li.append(anchor, button); list.append(li);
    });
  }
  function announce(message) {
    const status = root()?.querySelector('[data-rf-wishlist-status]');
    if (status) { status.textContent = ''; requestAnimationFrame(() => { status.textContent = message || ''; }); }
  }
  let opener;
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const holder = root();
    if (!holder) return;
    const toggle = event.target.closest('[data-rf-wishlist-add]');
    const open = event.target.closest('[data-rf-wishlist-open]');
    const close = event.target.closest('[data-rf-wishlist-close]');
    const remove = event.target.closest('[data-rf-wishlist-remove]');
    const dialog = document.getElementById('RFWishlistDialog');
    if (toggle) {
      const item = { id: toggle.dataset.id, title: toggle.dataset.title || '', url: toggle.dataset.url, image: safeImage(toggle.dataset.image) };
      if (!validItem(item)) return;
      const items = readItems();
      const exists = items.some(existing => String(existing.id) === String(item.id));
      writeItems(exists ? items.filter(existing => String(existing.id) !== String(item.id)) : [item, ...items]);
      sync(); announce(exists ? holder.dataset.removedMessage : holder.dataset.savedMessage);
    }
    if (open && dialog && !dialog.open) { opener = open; renderList(); dialog.showModal(); }
    if (close && dialog?.open) { dialog.close(); opener?.focus(); }
    if (remove) {
      const allButtons = [...dialog.querySelectorAll('[data-rf-wishlist-remove]')];
      const index = allButtons.indexOf(remove);
      writeItems(readItems().filter(item => String(item.id) !== remove.dataset.rfWishlistRemove));
      sync(); renderList(); announce(holder.dataset.removedMessage);
      const nextButtons = [...dialog.querySelectorAll('[data-rf-wishlist-remove]')];
      (nextButtons[Math.min(index, nextButtons.length - 1)] || dialog.querySelector('[data-rf-wishlist-close]'))?.focus();
    }
    if (event.target === dialog) {
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    }
  });
  window.addEventListener('storage', event => {
    if (event.key === storageKey || event.key === null) { sync(); if (document.getElementById('RFWishlistDialog')?.open) renderList(); }
  });
  document.addEventListener('rf:wishlist-sync', sync);
  document.addEventListener('shopify:section:load', sync);
  // Observe newly inserted favorite buttons, including native combined-listing updates.
  let queued = false;
  const mutationObserver = new MutationObserver(records => {
    const relevant = records.some(record => [...record.addedNodes].some(node => node instanceof Element && (node.matches('[data-rf-wishlist-add]') || node.querySelector('[data-rf-wishlist-add]'))));
    if (relevant && !queued) { queued = true; queueMicrotask(() => { queued = false; sync(); }); }
  });
  mutationObserver.observe(document.body, { childList: true, subtree: true });
  sync();
})();
